import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import { hooks } from '@lifesize/clients.sdk';
import { pstnNumbersSelectors } from '@lifesize/nucleus';
import { colors } from '@lifesize/ux-assets';
import { FormControl, FormControlLabel, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import MicrophoneLevel from 'components/AvSettings/MicrophoneLevel';
import DeviceMute, { muteType } from 'components/AvSettings/DeviceMute';
import PSTNContainer from 'containers/PSTN/PSTNContainer';
import { AudioType, getDevicesByKind } from 'utils/deviceUtils';
import MicrophoneDropdown from './MicrophoneDropdown';
import SpeakerDropdown from './SpeakerDropdown';
import { toggleAudioTest } from 'actions/AvSettingsModal';
import { RootState } from 'store/reducers';

const messages = defineMessages({
  header: { id: 'components.avSettings.sectionHeader.AudioSettings', defaultMessage: 'Audio Settings' },
  tabDevice: { id: 'components.avSettings.tabLabel.DeviceAudio', defaultMessage: 'Device Audio' },
  tabPhone: { id: 'components.avSettings.tabLabel.Phone', defaultMessage: 'Phone' }
});

interface Props {
  extension?: string;
  handleConfigChange: Function;
  intl: { formatMessage: Function };
  isInCall?: boolean;
  isJoinFlowCall?: boolean;
}

export const AudioSettings = (props: Props) => {
  const { extension, handleConfigChange, intl, isInCall, isJoinFlowCall } = props;
  const classes = useStyles({});
  const devices = hooks.useDevices();
  const dispatch = useDispatch();
  const audioInputDevices = useMemo(() => getDevicesByKind(devices, 'audioinput'), [devices]);
  const mediaSettings = useSelector((state: RootState) => state?.app?.mediaSettings);
  const { audioDevice, localAudioMuted } = hooks.useMedia();
  const pstnNumbers = useSelector((state) => pstnNumbersSelectors.pstnNumbersWithCountryName(state));
  const showPSTNOption = !!pstnNumbers.length && extension && isJoinFlowCall;
  const [deviceId, setDeviceId] = useState();

  const handleAudioTypeChange = (event: React.ChangeEvent<{ value: string }>) => {
    handleConfigChange('audio', event?.target?.value);
  };

  useEffect(() => {
    if (!!audioDevice && deviceId !== audioDevice?.deviceId) {
      setDeviceId(audioDevice.deviceId);
    }
  }, [audioInputDevices, audioDevice, deviceId]);

  return (
    <section className={classes.container}>
      {/* <Typography component="h6" className={classes.heading}>
        {intl.formatMessage(messages.header)}
      </Typography> */}
      {showPSTNOption && (
        <section className={classes.audioOptions} style={{ flexDirection: 'row' }}>
          <FormControl component="fieldset">
            <RadioGroup row name="audioType" value={mediaSettings.audio} onChange={handleAudioTypeChange}>
              <FormControlLabel
                value={AudioType.computer}
                control={<Radio color="primary" data-testid={`computer-audio-radiobutton`} />}
                label={intl.formatMessage(messages.tabDevice)}
              />
              <FormControlLabel
                value={AudioType.phone}
                control={<Radio color="primary" data-testid={`phone-audio-radiobutton`} />}
                label={intl.formatMessage(messages.tabPhone)}
              />
            </RadioGroup>
          </FormControl>
        </section>
      )}
      {showPSTNOption && mediaSettings.audio === 'phone' ? (
        <section>
          <PSTNContainer extension={extension} isInCall={isInCall} isJoinFlowCall={isJoinFlowCall} />
        </section>
      ) : (
        <>
          <MicrophoneDropdown handleConfigChange={handleConfigChange} isJoinFlowCall={isJoinFlowCall} />
          {!isInCall && (
            <section>
              <DeviceMute
                handleChange={() => {
                  dispatch(toggleAudioTest(false));
                  handleConfigChange('localAudioMuted', !mediaSettings.localAudioMuted);
                }}
                isInCall={isInCall}
                isJoinFlowCall={isJoinFlowCall}
                isMuted={mediaSettings.localAudioMuted}
                type={muteType.mic}
              />
            </section>
          )}
          <section>
            <MicrophoneLevel
              microphoneEnabled={isInCall ? !localAudioMuted : !mediaSettings.localAudioMuted}
              microphoneDeviceId={deviceId}
            />
          </section>
          <SpeakerDropdown handleConfigChange={handleConfigChange} />
        </>
      )}
    </section>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    '& > section': {
      display: 'flex',
      flexDirection: 'column',

      '&:not(:first-child)': {
        marginTop: theme.spacing(2)
      }
    }
  },
  heading: {
    fontSize: '1.6rem'
  },
  subHeading: {
    fontSize: '1.2rem'
  },
  speakerTestBtn: {
    alignSelf: 'flex-end',
    color: colors.lifesizeBlue,
    border: 'none',
    marginTop: theme.spacing(0.5),
    outline: 'none',
    paddingRight: 0,
    textAlign: 'right'
  },
  audioOptions: {
    justifyContent: 'space-between'
  }
}));

export default injectIntl(AudioSettings);
